import { yupResolver } from '@hookform/resolvers/yup';
import type { SxProps, Theme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useFormSubmitContext } from '@openx/components-core/lib/Form/context/FormSubmitContext.tsx';
import { Form } from '@openx/components-core/lib/Form/Form.tsx';
import { FormContainer } from '@openx/components-core/lib/FormContainer/FormContainer.tsx';
import { FormErrors } from '@openx/components-core/lib/FormErrors/FormErrors.tsx';
import { scrollToFirstVisibleErrorInForm } from '@openx/components-core/lib/utils/scrollToFirstErrorInForm.ts';
import { getValidationErrors } from '@openx/components-form/getValidationErrors.ts';
import { onKeyDown } from '@openx/components-form/onKeyDown.ts';
import { useTargetingBlockingRules } from '@openx/components-package/components/PackageForm/targetingRules/useTargetingBlockingRules.ts';
import {
  type DealFormData,
  type DealFormDataWithTargeting,
  dealFormField,
  RETARGETING_AQUA_VALIDATION_ERROR,
} from '@openx/types/deal.ts';
import { PackageFields } from '@openx/types/package.ts';
import { type UseWatchName, useWatchManyDebounce } from '@openx/utils/hooks/useWatchDebounce.ts';
import { type JSX, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type FieldPath, type UseFormReturn, useForm, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DSP_ERROR_TYPE, SEAT_ERROR_TYPE } from '../components/DealParticipants/hooks/useCheckBuyerSeatRestriction.ts';
import { useDealContext } from '../utils/context/DealContext.tsx';
import { DealFormProvider, useDealFormContext } from './DealForm.context.tsx';
import { useDealFeeReminderPopup } from './hooks/useDealFeeReminderPopup.tsx';
import { useUpdateDealGroupMeta } from './hooks/useUpdateDealGroupMeta.ts';
import { getDealsLabels } from './utils/dealFormUtils.ts';
import { hasErrorsOfTypes } from './utils/hasErrorsOfTypes.ts';

/**
 * Outer Form intercepts submit, runs validateAll, and returns early when errors exist —
 * so handleSubmit's onInvalid callback never runs. Bridge form-level FormErrors for the
 * retargeting Aqua rule after a submit attempt.
 */
const RetargetingAquaFormErrorBridge = ({
  onBusinessLogicError,
}: {
  onBusinessLogicError: (message: string | null) => void;
}) => {
  const { t } = useTranslation();
  const { submitClickCount } = useFormSubmitContext();
  const { errors } = useFormState<DealFormDataWithTargeting>();
  const isShownRef = useRef(false);

  const metaErrorMessage = String(errors[dealFormField.METADATA]?.message ?? '');
  const isRetargetingAquaError = metaErrorMessage.startsWith(RETARGETING_AQUA_VALIDATION_ERROR);

  useEffect(() => {
    if (submitClickCount === 0) {
      return;
    }

    if (isRetargetingAquaError) {
      if (!isShownRef.current) {
        window.scrollTo({ behavior: 'smooth', top: 0 });
      }

      isShownRef.current = true;
      onBusinessLogicError(t('Aqua DSP must be selected when Retargeting segment applied to targeting'));
      return;
    }

    if (isShownRef.current) {
      isShownRef.current = false;
      onBusinessLogicError(null);
    }
  }, [submitClickCount, isRetargetingAquaError, onBusinessLogicError, t]);

  return null;
};

const blockSubmitTypes = new Set([DSP_ERROR_TYPE, SEAT_ERROR_TYPE]);

const packageAccountUidPath: FieldPath<DealFormDataWithTargeting> = `${dealFormField.TARGETING}.${PackageFields.ACCOUNT_UID}`;

type FieldError = {
  fieldId: keyof DealFormDataWithTargeting;
  error: string;
};

type DealFormProps = {
  initialValues: DealFormDataWithTargeting;
  isEdit: boolean;
  onSubmit: (values: DealFormDataWithTargeting) => Promise<void>;
  renderFields: () => ReactNode;
  onDealIdsChange?: (ids: string[]) => void;
  hidden?: boolean;
  onAccountChange: (accountUid: string) => void;
};

type DealFormContentProps = {
  formMethods: UseFormReturn<DealFormDataWithTargeting>;
  onSubmit: (values: DealFormDataWithTargeting) => void;
  renderFields: () => ReactNode;
  onDealIdsChange?: (ids: string[]) => void;
  fieldsErrors: FieldError[];
  fieldsWithErrors: Record<string, unknown>;
  onBusinessLogicError: (message: string | null) => void;
  isEdit: boolean;
  sx?: SxProps<Theme>;
};

export const DealForm = ({
  initialValues,
  isEdit,
  onSubmit,
  renderFields,
  onDealIdsChange,
  hidden,
  onAccountChange,
}: DealFormProps): JSX.Element => {
  const { t } = useTranslation();
  const { useDealFormSelector, useValidationSchema } = useDealContext();
  const { dealForm } = useDealFormSelector();
  const dealFormContext = useDealFormContext();
  const { pckgInitialValues, pckgUid, pckg, pckgIsViaInteraction, pckgIsInitialLoading } = dealFormContext;
  const dealValidationSchema = useValidationSchema({ initialDealState: dealForm.currentDeal, isEdit });

  const [onSubmitErrorMessage, setOnSubmitErrorMessage] = useState<string | null>(null);

  const formMethods = useForm<DealFormDataWithTargeting>({
    defaultValues: initialValues,
    mode: 'all',
    resolver: yupResolver(dealValidationSchema, {}, { rawValues: true }),
  });

  const observableAccountUid = useWatch({ name: dealFormField.ACCOUNT_UID, control: formMethods.control });

  const observableTargeting = useWatch<DealFormDataWithTargeting>({
    name: dealFormField.TARGETING,
    control: formMethods.control,
  });

  const { fieldsWithErrors } = useTargetingBlockingRules({
    targeting: observableTargeting[PackageFields.TARGETING],
    enabled: true,
  });

  const onSubmitHandler = useCallback(
    async (values: DealFormDataWithTargeting) => {
      setOnSubmitErrorMessage(null);

      try {
        await onSubmit(values);
      } catch (error) {
        setOnSubmitErrorMessage((error as Error).message);
      }
    },
    [onSubmit]
  );

  const syncPackageAccountUid = useCallback(
    (accountUid?: string | null) => {
      if (accountUid && accountUid !== formMethods.getValues(packageAccountUidPath)) {
        formMethods.setValue(packageAccountUidPath, accountUid);
      }
    },
    [formMethods.getValues, formMethods.setValue]
  );

  useEffect(() => {
    onAccountChange(observableAccountUid!);
    syncPackageAccountUid(observableAccountUid);
  }, [onAccountChange, observableAccountUid, syncPackageAccountUid]);

  /** Form was rendered before the initial package was loaded - its targeting has to be applied once available */
  const [isAwaitingInitialTargeting, setIsAwaitingInitialTargeting] = useState(!!pckgIsInitialLoading);
  const initialTargeting = initialValues[dealFormField.TARGETING];

  useEffect(() => {
    if (!isAwaitingInitialTargeting || pckgIsInitialLoading) {
      return;
    }

    if (!pckgIsViaInteraction) {
      formMethods.setValue(dealFormField.TARGETING, structuredClone(initialTargeting));
      syncPackageAccountUid(formMethods.getValues(dealFormField.ACCOUNT_UID));
    }

    setIsAwaitingInitialTargeting(false);
  }, [
    isAwaitingInitialTargeting,
    pckgIsInitialLoading,
    pckgIsViaInteraction,
    initialTargeting,
    formMethods.setValue,
    formMethods.getValues,
    syncPackageAccountUid,
  ]);

  /**
   * Re-trigger deal name validation whenever the account changes (e.g. "On Behalf Of" selection).
   * Deal name uniqueness is scoped per account, so the same name may be valid under one account and
   * invalid under another. Skip the initial render to avoid surfacing a "required" error on an
   * untouched empty name field.
   */
  const previousAccountUidRef = useRef<typeof observableAccountUid>(observableAccountUid);
  useEffect(() => {
    if (previousAccountUidRef.current === observableAccountUid) {
      return;
    }

    previousAccountUidRef.current = observableAccountUid;

    if (formMethods.getValues(dealFormField.NAME)) {
      formMethods.trigger(dealFormField.NAME);
    }
  }, [observableAccountUid, formMethods.getValues, formMethods.trigger]);

  useEffect(() => {
    /** On initial load, targeting is handled along with deal initialValues */
    if (!pckgIsViaInteraction) {
      return;
    }

    formMethods.setValue(dealFormField.TARGETING, structuredClone(pckgInitialValues));

    /** For Curated Package, this logic is handled within CuratedTargetingPackageTab.tsx  */
    if (pckg?.uid && !pckg?.is_curated) {
      formMethods.setValue(dealFormField.PACKAGE_UID, pckg?.uid);
      /** On "Existing Package" and "Curated Package" tab, if "package" is not selected - this field will be in "error" state.
       Thus - clear errors upon setting any non-null value */
      formMethods.clearErrors(dealFormField.PACKAGE_UID);
    }
  }, [
    pckgIsViaInteraction,
    pckgInitialValues,
    formMethods.setValue,
    formMethods.clearErrors,
    pckg?.uid,
    pckg?.is_curated,
  ]);

  useEffect(() => {
    /** On initial load, PACKAGE_UID is handled along with deal initialValues
     if, in deal initialValues, PACKAGE_UID is null and targeting is present that means another deal was used as template */
    if (!pckgIsViaInteraction) {
      return;
    }

    if (pckgUid === null) {
      formMethods.setValue(dealFormField.SEGMENT_PARTNER_REVENUE_METHOD, null, { shouldDirty: true });
      formMethods.setValue(dealFormField.PACKAGE_UID, pckgUid, { shouldDirty: true });
    }
  }, [pckgIsViaInteraction, pckgUid, formMethods.setValue]);

  const dealFormLabels = useMemo(() => getDealsLabels(t), [t]);
  const { fieldsErrors, formErrors } = useMemo(
    () => getValidationErrors(dealFormLabels, dealForm.error),
    [dealFormLabels, dealForm.error]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormContainer fullPage>
        <FormErrors
          errors={
            onSubmitErrorMessage ? [onSubmitErrorMessage] : formErrors.length ? formErrors : [dealForm.error?.message]
          }
        />

        <DealFormProvider
          {...dealFormContext}
          pckgIsInitialLoading={!!pckgIsInitialLoading || isAwaitingInitialTargeting}
        >
          <DealFormContent
            formMethods={formMethods}
            onSubmit={onSubmitHandler}
            renderFields={renderFields}
            onDealIdsChange={onDealIdsChange}
            fieldsErrors={fieldsErrors}
            fieldsWithErrors={fieldsWithErrors}
            onBusinessLogicError={setOnSubmitErrorMessage}
            isEdit={isEdit}
            sx={hidden ? { display: 'none' } : undefined}
          />
        </DealFormProvider>
      </FormContainer>
    </LocalizationProvider>
  );
};

const DealFormContent = ({
  formMethods,
  onSubmit,
  renderFields,
  onDealIdsChange,
  fieldsErrors,
  fieldsWithErrors,
  onBusinessLogicError,
  isEdit,
  sx,
}: DealFormContentProps) => {
  const { verifyFeeReminderContinuation } = useDealFeeReminderPopup();
  const { pckgIsInitialLoading } = useDealFormContext();
  const { errors } = useFormState({ control: formMethods.control });

  useUpdateDealGroupMeta({
    control: formMethods.control,
    setValue: formMethods.setValue,
    trigger: formMethods.trigger,
  });

  const dealIdsToWatch: UseWatchName<DealFormData> = isEdit
    ? [dealFormField.DEAL_ID]
    : [dealFormField.CUSTOM_DEAL_ID, dealFormField.EXTERNAL_DEAL_ID];

  const observableDealIds = useWatchManyDebounce<DealFormDataWithTargeting>({
    name: dealIdsToWatch,
    control: formMethods.control,
  });

  useEffect(() => {
    onDealIdsChange?.(observableDealIds as string[]);
  }, [observableDealIds, onDealIdsChange]);

  useEffect(() => {
    if (fieldsErrors.length) {
      fieldsErrors.forEach(({ fieldId, error }) => {
        formMethods.setError(fieldId, { message: error, type: 'manual' });
      });

      window.scrollTo({ behavior: 'smooth', top: 0 });
    }
  }, [fieldsErrors, formMethods]);

  const onSubmitForm = async (values: DealFormDataWithTargeting): Promise<void> => {
    /** Targeting in the form values is a placeholder until the initial package is applied */
    const shouldBlockSubmit = !!pckgIsInitialLoading || hasErrorsOfTypes(errors, blockSubmitTypes);

    if (shouldBlockSubmit) {
      return;
    }

    onBusinessLogicError(null);

    const businessLogicErrors = Object.values(fieldsWithErrors);

    if (businessLogicErrors.length > 0) {
      const errorMessage = String(businessLogicErrors[0]);
      const formElement = document.querySelector('[data-test="deals-form"]');

      onBusinessLogicError(errorMessage);

      scrollToFirstVisibleErrorInForm({
        root: formElement ?? document,
      });

      return;
    }

    const continueSubmition = await verifyFeeReminderContinuation(values);

    if (continueSubmition) {
      return onSubmit(values);
    }
  };

  return (
    <Form
      methods={formMethods}
      data-test="deals-form"
      autoComplete="off"
      noValidate
      onSubmit={formMethods.handleSubmit(onSubmitForm)}
      onKeyDown={onKeyDown}
      sx={sx}
      name="deal"
    >
      <RetargetingAquaFormErrorBridge onBusinessLogicError={onBusinessLogicError} />
      {renderFields()}
    </Form>
  );
};
